import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { supabase } from '../integrations/supabase/client';
import { Users, Clock, ArrowRight } from 'lucide-react';

interface Usuario {
  user_id: string | null;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

interface FilaStatus {
  usuarios: Usuario[];
  proximo_usuario: Usuario | null;
  last_assigned_user_id: string | null;
}

export const FilaDistribuicao = () => {
  console.log('📋 FilaDistribuicao: Inicializando componente...');
  
  try {
    const [filaStatus, setFilaStatus] = useState<FilaStatus>({
    usuarios: [],
    proximo_usuario: null,
    last_assigned_user_id: null
  });
  const [loading, setLoading] = useState(true);

  const fetchFilaStatus = async () => {
    try {
      // Buscar configuração atual da fila
      const { data: queueData } = await supabase
        .from('lead_distribution_queue')
        .select('last_assigned_user_id')
        .limit(1)
        .single();

      // Buscar todos os usuários ativos ordenados
      const { data: usuarios } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name, email, role')
        .in('role', ['diretor', 'gerente', 'corretor'])
        .eq('is_active', true)
        .order('user_id');

      if (usuarios) {
        // Filtrar apenas usuários com user_id válido
        const usuariosValidos = usuarios.filter(u => u.user_id !== null) as Usuario[];
        let proximo_usuario = null;
        
        if (queueData?.last_assigned_user_id && usuariosValidos.length > 0) {
          // Encontrar posição atual
          const currentIndex = usuariosValidos.findIndex(u => u.user_id === queueData.last_assigned_user_id);
          
          if (currentIndex !== -1) {
            // Próximo usuário (ou volta para o primeiro se for o último)
            const nextIndex = currentIndex + 1 >= usuariosValidos.length ? 0 : currentIndex + 1;
            proximo_usuario = usuariosValidos[nextIndex];
          }
        } else if (usuariosValidos.length > 0) {
          // Se nunca foi atribuído, próximo é o primeiro
          proximo_usuario = usuariosValidos[0];
        }

        setFilaStatus({
          usuarios: usuariosValidos,
          proximo_usuario,
          last_assigned_user_id: queueData?.last_assigned_user_id || null
        });
      }
    } catch (error) {
      console.error('Erro ao buscar status da fila:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilaStatus();

    // Real-time updates para mudanças na fila
    const channel = supabase
      .channel('fila_changes')
      .on('postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'lead_distribution_queue' },
        () => fetchFilaStatus()
      )
      .on('postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'profiles' },
        () => fetchFilaStatus()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'diretor': return 'bg-red-100 text-red-800';
      case 'gerente': return 'bg-blue-100 text-blue-800';
      case 'corretor': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'diretor': return 'Diretor';
      case 'gerente': return 'Gerente';
      case 'corretor': return 'Corretor';
      default: return role;
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Fila de Distribuição</h3>
        </div>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-2">Carregando fila...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Users className="h-5 w-5" />
        <h3 className="text-lg font-semibold">Fila de Distribuição de Leads</h3>
      </div>

      {filaStatus.proximo_usuario && (
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-primary" />
            <span className="font-medium text-primary">Próximo na Fila:</span>
          </div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">
                    {filaStatus.proximo_usuario.first_name} {filaStatus.proximo_usuario.last_name}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">{filaStatus.proximo_usuario.email}</p>
                </div>
                <Badge className={`${getRoleBadgeColor(filaStatus.proximo_usuario.role)} shrink-0`}>
                  {getRoleLabel(filaStatus.proximo_usuario.role)}
                </Badge>
              </div>
        </div>
      )}

      <div className="space-y-2">
        <h4 className="font-medium text-sm text-muted-foreground mb-3">Ordem Completa da Fila:</h4>
        {filaStatus.usuarios.map((usuario, index) => {
          const isUltimo = usuario.user_id === filaStatus.last_assigned_user_id;
          const isProximo = usuario.user_id === filaStatus.proximo_usuario?.user_id;
          
          return (
            <div
              key={usuario.user_id}
              className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                isProximo ? 'bg-primary/5 border-primary/30' : 
                isUltimo ? 'bg-yellow-50 border-yellow-200' : 
                'bg-background border-border'
              }`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted text-sm font-medium shrink-0">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {usuario.first_name} {usuario.last_name}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">{usuario.email}</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1 sm:gap-2 shrink-0">
                <Badge className={`${getRoleBadgeColor(usuario.role)} text-xs px-2 py-1`}>
                  {getRoleLabel(usuario.role)}
                </Badge>
                <div className="flex items-center gap-1">
                  {isUltimo && (
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300 text-xs px-2 py-1">
                      Último
                    </Badge>
                  )}
                  {isProximo && (
                    <div className="flex items-center gap-1">
                      <ArrowRight className="h-3 w-3 text-primary" />
                      <Badge className="bg-primary text-primary-foreground text-xs px-2 py-1">
                        Próximo
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-3 bg-muted/50 rounded-lg">
        <p className="text-sm text-muted-foreground">
          <strong>Como funciona:</strong> Os leads são distribuídos sequencialmente entre todos os usuários ativos.
          Quando um lead não tem corretor responsável definido, vai para o próximo da fila.
        </p>
      </div>
    </Card>
  );
  } catch (error) {
    console.error('🚨 FilaDistribuicao: Erro no componente:', error);
    throw error; // Re-throw para que o ErrorBoundary capture
  }
};